import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { CrossmatchService } from '../../../services/crossmatch.service';
import { Recipient } from '../../../interfaces/recipient';
import { Donor } from '../../../interfaces/donor';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { PairService } from '../../../services/pair.service';
import { ParticipantService } from '../../../services/participant.service';
import { Pair } from '../../../interfaces/pair';
import { NotifyService } from '../../../common/notify/notify.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-add-crossmatch',
  imports: [
    MatFormFieldModule,
    MatDialogModule,
    MatInputModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatMomentDateModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatIconModule
  ],
  templateUrl: './add-crossmatch.component.html',
  styleUrl: './add-crossmatch.component.scss',
  providers: [NotifyService, { provide: DateAdapter, useClass: MomentDateAdapter }, { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }]
})
export class AddCrossmatchComponent {
  donorName?: string;
  recipientName?: string;
  type?: CrossmatchType;
  sampleDate?: string;

  matchrunForm!: FormGroup;
  donorSearchControl = new FormControl(''); // Separate control for search
  recipientSearchControl = new FormControl('');

  readonly title: string;
  readonly showType: boolean;

  pairs: Pair[] = [];
  donors: Donor[] = [];
  recipients: Recipient[] = [];
  filteredDonors = this.donors;
  filteredRecipients = this.recipients;
  selectedDonor: any = null;
  selectedRecipient: any = null;
  isFromMatchrun: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: AddCrossmatchDialogData,
    private readonly dialogRef: MatDialogRef<AddCrossmatchComponent>,
    private readonly crossMatchService: CrossmatchService,
    private pairService: PairService,
    private participantService: ParticipantService,
    private fb: FormBuilder,
    private readonly notify: NotifyService
  ) {
    this.type = data.type;
    this.title = data.matchChainContext ? 'Add Crossmatch for Match Chain' : (data.type ? `Add ${data.type} Crossmatch` : 'Add Crossmatch');
    this.isFromMatchrun = !!data.matchChainContext;

    this.showType = !data.type;
    this.matchrunForm = this.fb.group({
      donor: [null, Validators.required],
      recipient: [null, Validators.required],
      sampleDate: ['', Validators.required],
      resultDate: ['', Validators.required],
      positive: [isPositive(this.type)],
      excluded: [false],
      comments: ['']
    });

    this.loadPairsAndParticipants();

    // Handle preselected participants
    if (data.preselectedRecipient) {
      this.selectedRecipient = data.preselectedRecipient;
      this.recipientSearchControl.setValue(this.displayRecipientName(data.preselectedRecipient));
      this.matchrunForm.get('recipient')?.setValue(data.preselectedRecipient.id);

      // Disable recipient field when from matchrun
      if (this.isFromMatchrun) {
        this.recipientSearchControl.disable();
      }
    }

    if (data.preselectedDonor) {
      this.selectedDonor = data.preselectedDonor;
      this.donorSearchControl.setValue(this.displayDonorName(data.preselectedDonor));
      this.matchrunForm.get('donor')?.setValue(data.preselectedDonor.id);

      // Disable donor field when from matchrun
      if (this.isFromMatchrun) {
        this.donorSearchControl.disable();
      }
    }
  }

  async addCrossmatch() {
    if (this.matchrunForm.valid) {
      const matchrunData = this.matchrunForm.value;
      console.log(matchrunData);
      this.crossMatchService.createCrossmatch(matchrunData).subscribe({
        next: (response) => {
          console.log(this.type + ' crossmatch created:', response);
          this.notify.withAutodismiss(this.type + ` crossmatch created successfully!`);
          this.dialogRef.close(response);
        },
        error: (err) => {
          console.error('Error creating crossmatch:', err);
          this.notify.withAutodismiss('Failed to create crossmatch. Please try again.');
        }
      });
    } else {
      this.notify.withAutodismiss('Please fill out all required fields correctly.');
    }
  }

  private async loadPairsAndParticipants(): Promise<void> {
    // Load pairs first
    await this.loadPairs();

    // Then load and filter participants
    this.loadParticipants();
  }

  private async loadPairs(): Promise<void> {
    return new Promise((resolve) => {
      this.pairService.getAllPairs().subscribe((pairs) => {
        this.pairs = pairs;
        resolve();
      });
    });
  }

  private loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe((participants) => {
      // Filter participants based on pairings
      participants.forEach((participant) => {
        if (participant.type === 'Donor') {
          this.donors.push(participant as Donor);
        } else if (participant.type === 'Recipient') {
          this.recipients.push(participant as Recipient);
        }
      });
    });
  }
  filterDonors() {
    const searchValue = this.donorSearchControl.value!.toLowerCase();
    this.filteredDonors = this.donors.filter((donor) =>
      `${donor.enisNumber} ${donor.firstName} ${donor.lastName}`.toLowerCase().includes(searchValue)
    );
  }

  filterRecipients() {
    const searchValue = this.recipientSearchControl.value!.toLowerCase();
    this.filteredRecipients = this.recipients.filter((recipient) =>
      `${recipient.enisNumber} ${recipient.firstName} ${recipient.lastName}`.toLowerCase().includes(searchValue)
    );
  }

  displayDonorName(donor: Donor | string | null): string {
    if (typeof donor === 'string') {
      return donor;
    }
    if (!donor) {
      return '';
    }
    const firstName = donor.firstName || '';
    const lastName = donor.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${donor.enisNumber} - ${fullName}` : donor.enisNumber || '';
  }

  displayRecipientName(recipient: Recipient | string | null): string {
    if (typeof recipient === 'string') {
      return recipient;
    }
    if (!recipient) {
      return '';
    }
    const firstName = recipient.firstName || '';
    const lastName = recipient.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${recipient.enisNumber} - ${fullName}` : recipient.enisNumber || '';
  }

  selectDonor(donor: Donor) {
    this.selectedDonor = donor;
    this.donorSearchControl.setValue(this.displayDonorName(donor));
    this.matchrunForm.get('donor')?.setValue(donor.id); // Set selected donor ID in the form
  }

  selectRecipient(recipient: Recipient) {
    this.selectedRecipient = recipient;
    this.recipientSearchControl.setValue(this.displayRecipientName(recipient));
    this.matchrunForm.get('recipient')?.setValue(recipient.id); // Set selected recipient ID in the form
  }

  clearDonorSelection(event: Event) {
    this.selectedDonor = null;
    this.donorSearchControl.setValue('');
    this.matchrunForm.get('donor')?.reset();
    this.filteredDonors = this.donors;
  }

  clearRecipientSelection(event: Event) {
    this.selectedRecipient = null;
    this.recipientSearchControl.setValue('');
    this.matchrunForm.get('recipient')?.reset();
    this.filteredRecipients = this.recipients;
  }
}
export interface AddCrossmatchDialogData {
  type?: CrossmatchType;
  donorId?: string;
  recipientId?: string;
  dateEntered?: string;
  preselectedRecipient?: Recipient;
  preselectedDonor?: Donor;
  matchChainContext?: boolean;
}
export type CrossmatchType = 'Positive' | 'Negative';

export function isPositive(type?: CrossmatchType) {
  return type === 'Positive';
}

export interface AddCrosmatchDialogResult {
  donorId: string;
  recipientId: string;
  positive: boolean;
}
